// ============================================================================
// GST SUBMISSION SERVICE (ADR-0012 §1, ADR-0025 §3, §4, §8)
// ============================================================================
// A submission is what a received document FEEDS: one (GSTIN, period, branch,
// stream), parsed to staging rows with every row's classification. Creating a
// submission is parsing it; the scope check beforehand is what lets the UI say
// "this will replace revision 2" rather than ask a blind consolidate/replace.

import { get, post, put, downloadFile } from '../client/axiosClient';
import { gstEndpoints } from '../endpoints/gst.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { PaginatedResponse } from '../types';
import type {
  CreateGstSubmissionBatchRequest,
  CreateGstSubmissionRequest,
  GstDataSubmission,
  GstMappingField,
  GstMappingPreview,
  GstMappingProfile,
  SaveGstMappingProfileRequest,
  GstMappingPreviewRequest,
  GstProfileCandidate,
  GstSheetInfo,
  GstStagingRow,
  GstStagingSearchRequest,
  GstSubmissionScopeCheck,
  GstSubmissionSearchRequest,
} from '../types/gstEngagement.types';

const toPaginated = <T>(raw: any): PaginatedResponse<T> => {
  const paged = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: paged.items ?? [],
    pagination: {
      currentPage: paged.page ?? 1,
      pageSize: paged.pageSize ?? 20,
      totalPages: paged.totalPages ?? 0,
      totalItems: paged.totalCount ?? 0,
      hasNextPage: paged.hasNext ?? false,
      hasPreviousPage: paged.hasPrevious ?? false,
    },
  };
};

class GstSubmissionService {
  async getAll(engagementId: string, params?: GstSubmissionSearchRequest): Promise<PaginatedResponse<GstDataSubmission>> {
    const endpoint = params
      ? buildEndpointWithQuery(gstEndpoints.engagementSubmissionsBasePath(engagementId), params as Record<string, any>)
      : gstEndpoints.engagementSubmissions(engagementId);
    const response = await get<any>(endpoint);
    return toPaginated<GstDataSubmission>(response.data);
  }

  async getById(engagementId: string, submissionId: string): Promise<GstDataSubmission> {
    const response = await get<any>(gstEndpoints.engagementSubmissionById(engagementId, submissionId));
    return response.data?.data as GstDataSubmission;
  }

  /** The sheets in a received document, with whether each has a table. Reads the file; writes nothing. */
  async listSheets(engagementId: string, sourceDocumentId: string): Promise<GstSheetInfo[]> {
    const endpoint = buildEndpointWithQuery(
      gstEndpoints.engagementSubmissionSheetsBasePath(engagementId), { sourceDocumentId });
    const response = await get<any>(endpoint);
    return (response.data?.data ?? []) as GstSheetInfo[];
  }

  /**
   * Before creating: would this add alongside, or replace a live revision?
   * Given the document and sheets, also says whether it repeats or corrects
   * a parse of the same file (`reparse`).
   */
  async checkScope(
    engagementId: string,
    scope: {
      registrationId: string; taxPeriodStart: string; taxPeriodEnd: string; sourceRef: string; documentType: string;
      sourceDocumentId?: string; sheetNames?: string[];
    },
  ): Promise<GstSubmissionScopeCheck> {
    const { sheetNames, ...rest } = scope;
    const query: Record<string, any> = { ...rest };
    if (sheetNames && sheetNames.length > 0) query.sheetNames = sheetNames.join('|');
    const endpoint = buildEndpointWithQuery(
      gstEndpoints.engagementSubmissionScopeCheckBasePath(engagementId), query);
    const response = await get<any>(endpoint);
    return response.data?.data as GstSubmissionScopeCheck;
  }

  /**
   * Creates and parses. A 409 means the scope already has a live submission
   * and `confirmSupersede` was not set; the message names what would be replaced.
   */
  async create(engagementId: string, request: CreateGstSubmissionRequest): Promise<{ submission: GstDataSubmission; message: string }> {
    const response = await post<any>(gstEndpoints.engagementSubmissions(engagementId), request);
    return {
      submission: response.data?.data as GstDataSubmission,
      message: response.data?.message ?? 'Parsed.',
    };
  }

  /** One submission per stream from one file and one scope, in one transaction. 409 lists what would be replaced. */
  async createBatch(engagementId: string, request: CreateGstSubmissionBatchRequest): Promise<{ submissions: GstDataSubmission[]; message: string }> {
    const response = await post<any>(gstEndpoints.engagementSubmissionBatch(engagementId), request);
    return {
      submissions: (response.data?.data ?? []) as GstDataSubmission[],
      message: response.data?.message ?? 'Parsed.',
    };
  }

  /** Branches already used (or declared) for a GSTIN under this engagement. */
  async getBranches(engagementId: string, registrationId: string): Promise<string[]> {
    const endpoint = buildEndpointWithQuery(gstEndpoints.engagementSubmissionBranchesBasePath(engagementId), { registrationId });
    const response = await get<any>(endpoint);
    return (response.data?.data ?? []) as string[];
  }

  /** Profiles for the submission's stream, each with whether it fits the file. */
  async getMappingCandidates(engagementId: string, submissionId: string): Promise<GstProfileCandidate[]> {
    const response = await get<any>(gstEndpoints.engagementSubmissionMappingCandidates(engagementId, submissionId));
    return (response.data?.data ?? []) as GstProfileCandidate[];
  }

  /** Maps the staged rows with a profile (or draft) — totals over all records, one page of items. Writes nothing. */
  async previewMapping(engagementId: string, submissionId: string, request: GstMappingPreviewRequest): Promise<GstMappingPreview> {
    const response = await post<any>(gstEndpoints.engagementSubmissionMappingPreview(engagementId, submissionId), request);
    return response.data?.data as GstMappingPreview;
  }

  /** Records the profile on the submission. */
  async selectMappingProfile(engagementId: string, submissionId: string, profileId: string): Promise<GstDataSubmission> {
    const response = await put<any>(gstEndpoints.engagementSubmissionMappingProfile(engagementId, submissionId), { profileId });
    return response.data?.data as GstDataSubmission;
  }

  /** Saves the mapper's definition as the client's profile, PROPOSED; by default also records it on the submission. */
  async saveMappingProfile(engagementId: string, submissionId: string, request: SaveGstMappingProfileRequest): Promise<{ profile: GstMappingProfile; message: string }> {
    const response = await post<any>(gstEndpoints.engagementSubmissionMappingProfiles(engagementId, submissionId), request);
    return { profile: response.data?.data as GstMappingProfile, message: response.data?.message ?? 'Saved.' };
  }

  /** PROPOSED → ACCEPTED; the previous accepted version is retired. */
  async acceptMappingProfile(engagementId: string, profileId: string): Promise<GstMappingProfile> {
    const response = await put<any>(gstEndpoints.engagementMappingProfileAccept(engagementId, profileId), {});
    return response.data?.data as GstMappingProfile;
  }

  /** PROPOSED → REJECTED with a reason; submissions not yet accepted stop using it. */
  async rejectMappingProfile(engagementId: string, profileId: string, reason: string): Promise<GstMappingProfile> {
    const response = await put<any>(gstEndpoints.engagementMappingProfileReject(engagementId, profileId), { reason });
    return response.data?.data as GstMappingProfile;
  }

  /** The field vocabulary a profile may map to. */
  async getMappingFields(): Promise<GstMappingField[]> {
    const response = await get<any>(gstEndpoints.mappingFields());
    return (response.data?.data ?? []) as GstMappingField[];
  }

  /** Streams that have a downloadable standard template. */
  async getTemplateTypes(): Promise<string[]> {
    const response = await get<any>(gstEndpoints.mappingTemplates());
    return (response.data?.data ?? []) as string[];
  }

  /** Downloads the standard template for a stream (.xlsx, built from its system profile). */
  downloadTemplate(documentType: string): Promise<void> {
    return downloadFile(gstEndpoints.mappingTemplate(documentType), `PRIMA-${documentType}-template.xlsx`);
  }

  /** The staging rows in file order, each with its classification. */
  async getStaging(
    engagementId: string, submissionId: string, params?: GstStagingSearchRequest,
  ): Promise<PaginatedResponse<GstStagingRow>> {
    const endpoint = buildEndpointWithQuery(
      gstEndpoints.engagementSubmissionStagingBasePath(engagementId, submissionId), (params ?? {}) as Record<string, any>);
    const response = await get<any>(endpoint);
    return toPaginated<GstStagingRow>(response.data);
  }
}

export const gstSubmissionService = new GstSubmissionService();
export default gstSubmissionService;
